<template>
    <PainelFull ref="painelRef" class="editor-landing-wrapper">
        <template #cabecalho>
            <h5 style="margin-top: 0; margin-bottom: 0; padding-left: 12px">
                <font-awesome-icon icon="columns" />
                Personalização de Landing Page
            </h5>
            <ControlledStepper v-model="passoAtual" :steps="listaDePassos" />
        </template>

        <template #corpo>
            <div class="editor-landing-conteudo">
                <DebugOverlay :estaAberto="mostrarDebug" />
                <component
                    :is="componenteAtual"
                    :ref="
                        (el) => {
                            if (componenteAtual === Publicacao) {
                                publicacaoRef = el;
                            } else if (componenteAtual === ConfigLandingPage) {
                                configLandingPageRef = el;
                            }
                        }
                    "
                    :initialData="componenteAtual === ConfigLandingPage ? seoData : undefined"
                    @update:data="componenteAtual === ConfigLandingPage ? atualizarSeoData : () => {}"
                />
            </div>
        </template>

        <template #rodape>
            <div class="editor-rodape">
                <div class="editor-rodape-botoes">
                    <BtnVoltar @click="voltarPasso" />
                    <Botao
                        :disabled="!landingPageId"
                        :loading="statusPreview === STATUS_PREVIEW_ENUM.ABRINDO"
                        loadingLabel="Abrindo..."
                        :error="statusPreview === STATUS_PREVIEW_ENUM.ERRO"
                        errorLabel="Erro ao abrir"
                        :success="statusPreview === STATUS_PREVIEW_ENUM.ABERTO"
                        successLabel="Aberto com sucesso"
                        @click="lidarComClickPreview"
                        label="Pré-Visualizar"
                        icon="visibility"
                    />
                    <Botao
                        :disabled="dirtyState === false"
                        :loading="statusSalvamento === STATUS_SALVAMENTO_ENUM.SALVANDO"
                        loadingLabel="Salvando..."
                        :error="statusSalvamento === STATUS_SALVAMENTO_ENUM.ERRO"
                        errorLabel="Erro ao salvar"
                        :success="statusSalvamento === STATUS_SALVAMENTO_ENUM.SUCESSO"
                        successLabel="Salvo com sucesso"
                        @click="lidarComClickSalvar"
                        :label="dirtyState === false ? 'Nenhuma Alteração' : 'Salvar Rascunho'"
                        icon="save"
                    />
                    <Button @click="toggle" class="p-button-outlined">
                        <span class="material-symbols-outlined select-none" style="font-size: 16px">
                            {{ isFullscreen ? 'close_fullscreen' : 'open_in_full' }}
                        </span>
                        <span>
                            {{ isFullscreen ? 'Sair de Tela Cheia' : 'Tela Cheia' }}
                        </span>
                    </Button>
                    <Button v-if="ehDev" label="JSON Estrutura da Página" icon="pi pi-info-circle" @click="mostrarDebug = !mostrarDebug" />
                    <Button v-if="!isLastStep" label="Próximo" iconPos="right" icon="pi pi-chevron-right" @click="proximoPasso" />
                    <Button v-else label="Publicar" class="p-button-primary" icon="pi pi-send" @click="handlePublicar" :disabled="dirtyState" />
                </div>
            </div>
        </template>
    </PainelFull>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { useFullscreen } from '@vueuse/core';
import { STATUS_SALVAMENTO_ENUM, STATUS_PREVIEW_ENUM } from '@/store/landingPage.js';
import { useConfirm } from 'primevue/useconfirm';
import PainelFull from '@/components/editorLandingPage/ui/PainelFull.vue';
import ControlledStepper from '@/components/editorLandingPage/ui/ControlledStepper.vue';
import Botao from '@/components/editorLandingPage/ui/Botao.vue';
import ConfigLandingPage from '@/components/editorLandingPage/core/ConfigLandingPage.vue';
import Editor from '@/components/editorLandingPage/core/Editor.vue';
import Publicacao from '@/components/editorLandingPage/core/Publicacao.vue';
import ConfigFormulario from '@/components/editorLandingPage/core/ConfigFormulario.vue';
import DebugOverlay from '@/components/editorLandingPage/core/debug/DebugOverlay.vue';

import { useEditorLandingPage } from '@/composables/useEditorLandingPage';

const ehDev = process.env.NODE_ENV === 'development';
const mostrarDebug = ref(false);

const configLandingPageRef = ref();
const publicacaoRef = ref();
const passoAtual = ref(0);
const painelRef = ref(null);

const confirm = useConfirm();

const { salvarPaginaAtual, abrirPreview, statusSalvamento, statusPreview, dirtyState, temFormulario, publicarLandingPage, selecionarElemento } =
    useEditorLandingPage();
const { toggle, isFullscreen } = useFullscreen(painelRef);
const route = useRoute();
const router = useRouter();

const listaDePassos = computed(() => {
    const passos = [{ label: 'Edição de página' }];

    if (temFormulario.value) {
        passos.push({ label: 'Formulário' });
    }

    passos.push({ label: 'Configurações' }, { label: 'Publicação' });

    return passos;
});

const componentesDisponiveis = computed(() => {
    const componentes = [Editor];

    if (temFormulario.value) {
        componentes.push(ConfigFormulario);
    }

    componentes.push(ConfigLandingPage, Publicacao);
    return componentes;
});

const componenteAtual = computed(() => {
    return componentesDisponiveis.value[passoAtual.value];
});

const landingPageId = computed(() => route.params.landingPageId);
const isLastStep = computed(() => passoAtual.value >= listaDePassos.value.length - 1);

const lidarComClickSalvar = async () => {
    salvarPaginaAtual();
};

const handlePublicar = async () => {
    await publicarLandingPage();
};

const lidarComClickPreview = async () => {
    await abrirPreview({
        eventoId: route.params.eventoId,
        landingPageId: route.params.landingPageId,
    });
};

watch(passoAtual, () => {
    selecionarElemento(null);
});

const voltarPasso = () => {
    if (passoAtual.value === 0) {
        router.push(`/checkins/eventos/${route.params.eventoId}`);
    } else {
        passoAtual.value--;
    }
};

const proximoPasso = async () => {
    if (!isLastStep.value) {
        passoAtual.value++;
    }
};

onBeforeRouteLeave((to, from, next) => {
    const rotasPermitidas = ['login', 'Checkins_Eventos_EditarLanding', 'error'];
    if (rotasPermitidas.includes(to.name)) {
        next();
        return;
    }

    if (!dirtyState.value) {
        next();
        return;
    } else {
        confirm.require({
            message: 'Você tem alterações não salvas. Deseja salvar antes de sair?',
            header: 'Confirmação de Saída',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Salvar e Sair',
            rejectLabel: 'Sair sem Salvar',
            accept: async () => {
                await salvarPaginaAtual();
                next();
            },
            reject: () => {
                next();
            },
        });
    }
});

const handleBeforeUnload = (event) => {
    if (dirtyState.value) {
        event.preventDefault();
        event.returnValue = '';
    }
};

onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
});

onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>

<style scoped lang="scss">
.editor-landing-wrapper {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.editor-landing-conteudo {
    flex-grow: 1;
    overflow-y: auto;
    min-height: 0;
    display: flex;
}

.editor-rodape {
    z-index: 10;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
}

.editor-rodape-botoes {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;
    padding: 16px 16px 16px calc(16px - 6.5px); // 6.5px é a margem do botão
}
</style>
